import React, { FC, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { KButton, KDropdown, KInput, KSelectDate, KSpan } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"

import { releasePlans, users } from "../../../apis"
import { USER_PERMISSION } from "../../../constants"
import { getLanguageJsonFor } from "../../../constants/languages"
import { RootState } from "../../../store"
import { CoManagerReleasePlan, CoManagerReleasePlanOptions, LabelBeatport } from "../../../types"
import { formatIsoDate, mapListToCamelCase, mapToCamelCase } from "../../../utility"
import ImageUploadModal from "../catalog/asset-registry/modal/ImageUploadModal"
import { COVER_TYPES, readCoverFile, releasePath } from "./ReleasePlanPanel"
import "./CoManager.css"

// idol's product type ids, the three the release registry prices.
const PRODUCT_TYPES = [
  { id: "2", word: "release_plan_type_single" },
  { id: "8", word: "release_plan_type_ep" },
  { id: "1", word: "release_plan_type_album" }
] as const
// A year of releases is about as many as anyone plans ahead, and the server refuses more.
const MAX_ROWS = 12
const COPYRIGHT_PATH = "/account/copyright-and-production"
const SINGLE = "2"
// The genres the registry asks a Beatport logo of the label for, matched the same way it does.
const BEATPORT_GENRES = ["Electronic", "Hip-Hop/Rap"]

type CoManagerWords = ReturnType<typeof getLanguageJsonFor>["co_manager"]

type PlanRow = {
  title: string
  productType: string
  // The songs of an EP or an album, as typed. A single has one.
  trackCount: string
  releaseDate?: Date
  labelId?: string
  metadataLanguageId?: string
  // Set once the artist picks the language themselves, after which the title no longer changes it.
  languagePicked?: boolean
  subgenreId?: string
  cover?: { name: string; dataUrl: string }
}

const fill = (template: string, values: { [key: string]: string }) =>
  Object.entries(values).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template)

// The date as the server reads it, from the calendar day the artist picked rather than from UTC,
// which would move a date picked late in the evening in Istanbul to the day before.
const isoDay = (day: Date) =>
  `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`

const emptyRow = (options?: CoManagerReleasePlanOptions | null): PlanRow => ({
  title: "",
  productType: PRODUCT_TYPES[0].id,
  trackCount: "",
  labelId: options?.defaults?.labelId || undefined,
  metadataLanguageId: options?.defaults?.metadataLanguageId || undefined,
  subgenreId: options?.defaults?.subgenreId || undefined
})

// How far apart a year's releases can be spread when the dates are worked out for the artist. Any
// other gap or count is typed into the box beside the chips.
const CADENCE_MONTHS = [1, 2, 3, 6]
const MAX_CADENCE_MONTHS = 12
const CADENCE_COUNTS = [1, 2, 3, 4, 6, 12]
const FRIDAY = 5

// Letters that give a title's language away, as the idol language name each points to. The server
// guesses the same way for a draft sent without one. An ö or ü is as German as it is Turkish, so only
// letters no other language here shares decide anything; otherwise the artist's own language stands.
const TITLE_LANGUAGES: [RegExp, string][] = [
  [/[ğĞıİşŞ]/, "turkish"],
  [/[äÄß]/, "german"],
  [/[ñÑ¿¡]/, "spanish"],
  [/[ãõÃÕ]/, "portuguese"],
  [/[\u3040-\u30ff]/, "japanese"],
  [/[\uac00-\ud7af]/, "korean"],
  [/[\u4e00-\u9fff]/, "chinese"],
  [/[\u0400-\u04ff]/, "russian"],
  [/[\u0600-\u06ff]/, "arabic"],
  [/[\u0370-\u03ff]/, "greek"],
  [/[\u0590-\u05ff]/, "hebrew"]
]

// Common English words. A title in plain Latin letters carrying one of them is taken as English, so
// "Summer Night" does not inherit the Turkish of the artist's last release.
const ENGLISH_WORDS = new Set(
  [
    "the and of to for with you your my i'm we us our it's is are be love night day baby heart life dream",
    "feel way go get know like never forever all one time girl boy summer winter rain fire light dark",
    "dance tonight home world without little sweet good bad"
  ]
    .join(" ")
    .split(" ")
)

const englishTitle = (title: string) =>
  title
    .toLowerCase()
    .split(/[^a-z']+/)
    .some((word) => ENGLISH_WORDS.has(word))

const guessLanguage = (title: string, languages: KSelectOption[], fallback?: string) => {
  const name =
    TITLE_LANGUAGES.find(([pattern]) => pattern.test(title))?.[1] || (englishTitle(title) ? "english" : undefined)
  const match = name && languages.find((language) => language.label.trim().toLowerCase() === name)
  return match ? match.value2 : fallback
}

/**
 * The release dates of a plan spread every few months from the first one. Each lands on the Friday on or
 * after it, the day the stores refresh their new release playlists on.
 */
const cadenceDates = (start: Date, months: number, count: number) =>
  Array.from({ length: count }, (_, index) => {
    const day = new Date(start.getFullYear(), start.getMonth() + index * months, start.getDate())
    day.setDate(day.getDate() + ((FRIDAY - day.getDay() + 7) % 7))
    return day
  })

/**
 * The card a release plan is made on. One row per release, so a year's albums are opened in one go,
 * or a release the artist already has, so a plan can be put on something already in the catalog.
 *
 * Saving opens the drafts at idol, which is slow and cannot be taken back from here, so the card
 * holds every field until the server has said yes and only then hands the plans up.
 *
 * The same card is on the release plan page and under a co-manager reply; source says which, and is
 * kept on the plan so the release plan channel can tell the two apart. Under a reply it opens with a
 * row for every release the artist already named in the conversation (initialTitles).
 */
const ReleasePlanCard: FC<{
  words: CoManagerWords
  onSaved: (plans: CoManagerReleasePlan[]) => void
  source?: "page" | "co_manager"
  initialTitles?: string[]
}> = ({ words, onSaved, source = "co_manager", initialTitles }) => {
  const navigate = useNavigate()
  const user = useSelector((state: RootState) => state.auth.user)
  const [options, setOptions] = useState<CoManagerReleasePlanOptions | null>(null)
  const [labelBeatportList, setLabelBeatportList] = useState<LabelBeatport[]>([])
  // The label the Beatport logo is asked for, and the labels the artist chose not to send to Beatport.
  const [beatportLabel, setBeatportLabel] = useState<{ value: string; label: string } | null>(null)
  const [beatportSkipped, setBeatportSkipped] = useState<string[]>([])
  const [mode, setMode] = useState<"new" | "existing">("new")
  const [rows, setRows] = useState<PlanRow[]>(() =>
    initialTitles?.length ? initialTitles.slice(0, MAX_ROWS).map((title) => ({ ...emptyRow(), title })) : [emptyRow()]
  )
  // The "every few months" helper, which fills the dates of the rows instead of the artist picking each.
  // The count is the number of rows itself, so picking one opens that many releases at once.
  const [cadenceStart, setCadenceStart] = useState<Date | undefined>(undefined)
  const [cadenceMonths, setCadenceMonths] = useState(CADENCE_MONTHS[1])
  // What is typed in the "other" boxes, kept as text so a half typed number is not snapped back.
  const [customMonths, setCustomMonths] = useState("")
  const [customCount, setCustomCount] = useState("")
  const [existingId, setExistingId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState("")

  const coverInputRef = useRef<HTMLInputElement | null>(null)
  // Which row the cover picker was opened from. One input serves every row.
  const [coverTarget, setCoverTarget] = useState<number | null>(null)

  useEffect(() => {
    releasePlans("/options/", "get")
      .then((response) => {
        const loaded = mapToCamelCase<CoManagerReleasePlanOptions>(response.data)
        setOptions(loaded)
        // Without the new release permission only a release already in the catalog can be planned.
        if (!loaded.canOpenDrafts) setMode("existing")
        // The label and language of the artist's last release, on every row that has none picked yet. A
        // title already typed in, as the names carried over from the conversation are, decides the
        // language first.
        const languages: KSelectOption[] = (loaded.metadataLanguages || []).map((language) => ({
          label: language.label,
          value: Number(language.value),
          value2: language.value
        }))
        setRows((previous) =>
          previous.map((row) => ({
            ...row,
            labelId: row.labelId || loaded.defaults?.labelId || undefined,
            metadataLanguageId:
              row.metadataLanguageId ||
              guessLanguage(row.title, languages, loaded.defaults?.metadataLanguageId || undefined),
            subgenreId: row.subgenreId || loaded.defaults?.subgenreId || undefined
          }))
        )
      })
      .catch(() => setError(words.release_plan_errors.label_unavailable))
    fetchLabelBeatportLabels()
  }, [])

  const fetchLabelBeatportLabels = () =>
    users("/label_camp_beatport_labels/", "get")
      .then((response) => setLabelBeatportList(mapListToCamelCase(response.data.results || [])))
      .catch(() => undefined)

  const minimumDate = new Date()
  minimumDate.setDate(minimumDate.getDate() + (options?.minLeadDays ?? 7))

  const labelOptions: KSelectOption[] = (options?.labels || []).map((label) => ({
    label: label.label,
    value: Number(label.value),
    value2: label.value
  }))
  const languageOptions: KSelectOption[] = (options?.metadataLanguages || []).map((language) => ({
    label: language.label,
    value: Number(language.value),
    value2: language.value
  }))
  const genreOptions: KSelectOption[] = (options?.genres || []).map((genre) => ({
    label: genre.label,
    value: Number(genre.value),
    value2: String(genre.value)
  }))
  const releaseOptions: KSelectOption[] = (options?.releases || []).map((release) => ({
    label: release.releaseDate ? `${release.title} · ${formatIsoDate(release.releaseDate)}` : release.title,
    value: release.id
  }))

  const updateRow = (index: number, change: Partial<PlanRow>) =>
    setRows((previous) => previous.map((row, at) => (at === index ? { ...row, ...change } : row)))

  // Every row's date from the first date and the gap. Titles already typed stay with their row.
  const spread = (previous: PlanRow[], start: Date | undefined, months: number) => {
    if (!start) return previous
    const dates = cadenceDates(start, months, previous.length)
    return previous.map((row, index) => ({ ...row, releaseDate: dates[index] }))
  }

  const pickStart = (day: Date | undefined) => {
    setCadenceStart(day)
    setRows((previous) => spread(previous, day, cadenceMonths))
  }

  const pickMonths = (months: number) => {
    setCadenceMonths(months)
    setRows((previous) => spread(previous, cadenceStart, months))
  }

  // As many rows as releases. The rows already there keep what was filled in, down to their dates; the
  // new ones take the next dates of the cadence when it has a first date.
  const pickCount = (count: number) => {
    const total = Math.min(Math.max(count, 1), MAX_ROWS)
    setRows((previous) => {
      const dates = cadenceStart ? cadenceDates(cadenceStart, cadenceMonths, total) : []
      return Array.from(
        { length: total },
        (_, index) => previous[index] || { ...emptyRow(options), releaseDate: dates[index] }
      )
    })
  }

  // A typed title moves the language with it, until the artist has picked one by hand.
  const changeTitle = (index: number, title: string) =>
    setRows((previous) =>
      previous.map((row, at) =>
        at !== index
          ? row
          : {
              ...row,
              title,
              metadataLanguageId: row.languagePicked
                ? row.metadataLanguageId
                : guessLanguage(title, languageOptions, options?.defaults?.metadataLanguageId || undefined)
            }
      )
    )

  const readCover = (file: File | undefined) => {
    const target = coverTarget
    if (!file || target === null) return
    readCoverFile(file, words.release_plan_errors).then(({ dataUrl, error }) => {
      if (!dataUrl) return setError(error || "")
      setError("")
      updateRow(target, { cover: { name: file.name, dataUrl } })
    })
  }

  const errorText = (data: { error?: string; detail?: { [key: string]: any } } | undefined) => {
    const messages = words.release_plan_errors as { [key: string]: string }
    const template = (data?.error && messages[data.error]) || messages.generic
    const detail = data?.detail || {}
    return fill(template, {
      days: String(detail.min_lead_days ?? options?.minLeadDays ?? 7),
      min: String(detail.min ?? ""),
      max: String(detail.max ?? MAX_ROWS),
      title: String(detail.title ?? ""),
      width: String(detail.width ?? ""),
      height: String(detail.height ?? ""),
      // idol's own message when it refused the draft, which is the only thing that says what to fix.
      detail: typeof detail.result === "string" ? detail.result : JSON.stringify(detail.result || "")
    })
  }

  const minTracks = options?.minTracks ?? 2
  const maxTracks = options?.maxTracks ?? 40
  const trackCountValid = (row: PlanRow) => {
    if (row.productType === SINGLE) return true
    const count = Number(row.trackCount)
    return Number.isInteger(count) && count >= minTracks && count <= maxTracks
  }

  // The label of a row whose genre needs a Beatport logo the label does not have yet, the same check the
  // registry makes before it lets General Information be saved.
  const labelMissingBeatportLogo = (skipped: string[]) => {
    const exempt = user.permissionUser?.some(
      (permissionUser: any) => permissionUser.permission?.id === USER_PERMISSION.DO_NOT_SEND_STATE51_BEATPORT
    )
    if (exempt) return null
    for (const row of rows) {
      const genre = genreOptions.find((option) => option.value2 === row.subgenreId)
      if (!genre || !BEATPORT_GENRES.some((name) => genre.label.includes(name))) continue
      const label = row.labelId
        ? options?.labels.find((option) => String(option.value) === row.labelId)
        : options?.labels.length === 1
        ? options.labels[0]
        : undefined
      if (!label || skipped.includes(String(label.value))) continue
      const beatport = labelBeatportList.find((item) => String(item.value) === String(label.value))
      if (!beatport?.labelBeatportLogo?.cover) return { value: String(label.value), label: label.label }
    }
    return null
  }

  const save = (skipped: string[] = beatportSkipped) => {
    setError("")
    let plans
    if (mode === "existing") {
      if (!existingId) return setError(words.release_plan_errors.release_not_found)
      const release = options?.releases.find((item) => item.id === existingId)
      plans = [{ release_id: existingId, release_date: release?.releaseDate }]
    } else {
      if (rows.some((row) => !row.title.trim())) return setError(words.release_plan_errors.title_required)
      if (rows.some((row) => !row.releaseDate)) return setError(words.release_plan_errors.release_date_invalid)
      if (rows.some((row) => !trackCountValid(row))) {
        return setError(
          fill(words.release_plan_errors.track_count_invalid, { min: String(minTracks), max: String(maxTracks) })
        )
      }
      if (rows.some((row) => !row.subgenreId)) return setError(words.release_plan_errors.genre_required)
      if (languageOptions.length && rows.some((row) => !row.metadataLanguageId)) {
        return setError(words.release_plan_errors.metadata_language_required)
      }
      if (labelOptions.length > 1 && rows.some((row) => !row.labelId)) {
        return setError(words.release_plan_errors.label_required)
      }
      const missingLogo = labelMissingBeatportLogo(skipped)
      if (missingLogo) return setBeatportLabel(missingLogo)
      plans = rows.map((row) => ({
        title: row.title.trim(),
        product_type: row.productType,
        track_count: row.productType === SINGLE ? 1 : Number(row.trackCount),
        release_date: row.releaseDate ? isoDay(row.releaseDate) : null,
        label_id: row.labelId,
        metadata_language_id: row.metadataLanguageId,
        subgenre_id: row.subgenreId,
        cover: row.cover?.dataUrl
      }))
    }

    setSaving(true)
    releasePlans("/", "post", { plans, source })
      .then((response) => {
        setSaved(true)
        onSaved(mapToCamelCase<{ results: CoManagerReleasePlan[] }>(response.data).results)
      })
      .catch((failure) => {
        const data = failure?.response?.data
        setError(errorText(data))
        // idol refused a draft partway down the list. The ones opened before it are saved plans with
        // UPCs of their own, so they go up now and leave the card, and a retry only sends the rest.
        const created: CoManagerReleasePlan[] = mapListToCamelCase(data?.detail?.created || [])
        if (created.length) {
          onSaved(created)
          setRows((previous) => previous.slice(created.length))
        }
      })
      .finally(() => setSaving(false))
  }

  const label = (text: string) => <KSpan text={text} fontSize={11} lineHeight="15px" color="#888" />

  // The picker as one button carrying the picked day, so the day itself opens the calendar and not only
  // the icon beside it. Its inline month strip is wider than the card, in the chat and on the page alike.
  const datePicker = (value: Date | undefined, onChange: (day: Date | undefined) => void, dataTestId: string) => (
    <div className="co-manager-plan-date" data-testid={dataTestId}>
      <KSelectDate
        icon="/analytics_icons/calendar.svg"
        buttonText={value ? formatIsoDate(isoDay(value)) : words.release_plan_date_pick}
        boxShadow="none"
        hideBody
        value={value}
        minimumDate={minimumDate}
        onChange={onChange}
        // Centred on the screen rather than anchored to the button: anchored, it opened inside the chat's
        // scroll area, which clipped it away on a scroll together with its Cancel and Apply, and left the
        // picker's full-screen backdrop over everything with no way out.
        width="auto"
        padding="6px 14px 6px 10px"
        backgroundColor="#FFF"
        hoverBackgroundColor="#FAFAFA"
        borderRadius={100}
      />
    </div>
  )

  // Keyed by its text, which is unique within each row of chips the card maps out.
  const chip = (text: string, selected: boolean, onClick: () => void, dataTestId?: string) => (
    <KButton
      key={text}
      dataTestId={dataTestId}
      text={text}
      onClick={onClick}
      disabled={saving}
      background={selected ? "#111" : "#FFF"}
      hoverBackground={selected ? "#333" : "#FAFAFA"}
      border={selected ? "1px solid #111" : "1px solid #EDEDED"}
      hoverBorder={selected ? "1px solid #333" : "1px solid #D6D6D6"}
      borderRadius={100}
      textColor={selected ? "#FFF" : "#111"}
      fontSize={12}
      fontWeight={400}
      width="auto"
      height="28px"
      padding="5px 12px"
      shadowDisabled
    />
  )

  // The "other" box at the end of a chip row, for a gap or a count the chips do not offer. Whole
  // numbers from 1 to max only; anything else stays in the box without changing the plan.
  const customBox = (
    value: string,
    placeholder: string,
    max: number,
    onChange: (text: string, value: number | null) => void,
    dataTestId: string
  ) => (
    <div className="co-manager-plan-custom">
      <KInput
        dataTestId={dataTestId}
        type="number"
        value={value}
        placeholder={placeholder}
        padding="4px 12px"
        borderRadius={100}
        fontSize="12px"
        // Outlined like a picked chip once it holds a number, since the chips beside it are then off.
        border={value ? "1px solid #111" : undefined}
        disabled={saving}
        onChange={(text) => {
          const number = Number(text)
          onChange(text, Number.isInteger(number) && number >= 1 && number <= max ? number : null)
        }}
      />
    </div>
  )

  const renderRow = (row: PlanRow, index: number) => (
    <div className="co-manager-plan-row" key={`plan-row-${index}`}>
      <div className="co-manager-plan-row-head">
        <KInput
          value={row.title}
          placeholder={words.release_plan_name}
          padding="10px"
          onChange={(value) => changeTitle(index, value)}
          disabled={saving}
        />
        {rows.length > 1 && (
          <KButton
            dataTestId="co-manager-plan-remove-row"
            icon="/admin_icons/close.svg"
            onClick={() => {
              setRows((previous) => previous.filter((_, at) => at !== index))
              setCustomCount("")
            }}
            disabled={saving}
            background="transparent"
            hoverBackground="#EFEFEF"
            borderRadius={100}
            width="28px"
            height="28px"
            padding="6px"
            shadowDisabled
          />
        )}
      </div>
      <div className="co-manager-plan-chips">
        {PRODUCT_TYPES.map((type) =>
          chip(words[type.word], row.productType === type.id, () => updateRow(index, { productType: type.id }))
        )}
      </div>
      {/* An EP or an album says how many songs it has, so the song step asks for every one of them. */}
      {row.productType !== SINGLE && (
        <>
          {label(fill(words.release_plan_track_count_hint, { min: String(minTracks), max: String(maxTracks) }))}
          <KInput
            dataTestId="co-manager-plan-track-count"
            type="number"
            value={row.trackCount}
            placeholder={words.release_plan_track_count}
            padding="10px"
            onChange={(value) => updateRow(index, { trackCount: value })}
            disabled={saving}
          />
        </>
      )}
      {label(words.release_plan_date)}
      {datePicker(row.releaseDate, (day) => updateRow(index, { releaseDate: day }), "co-manager-plan-date")}
      {labelOptions.length > 1 && (
        <KDropdown
          dataTestId="co-manager-plan-label"
          options={labelOptions}
          selected={labelOptions.find((option) => option.value2 === row.labelId)}
          onSelect={(selected) => updateRow(index, { labelId: (selected as KSelectOption)?.value2 })}
          placeholder={words.release_plan_label}
        />
      )}
      {!!genreOptions.length && (
        <KDropdown
          dataTestId="co-manager-plan-genre"
          options={genreOptions}
          selected={genreOptions.find((option) => option.value2 === row.subgenreId)}
          onSelect={(selected) => updateRow(index, { subgenreId: (selected as KSelectOption)?.value2 })}
          placeholder={words.release_plan_genre}
        />
      )}
      {!!languageOptions.length && (
        <KDropdown
          dataTestId="co-manager-plan-language"
          options={languageOptions}
          selected={languageOptions.find((option) => option.value2 === row.metadataLanguageId)}
          onSelect={(selected) =>
            updateRow(index, { metadataLanguageId: (selected as KSelectOption)?.value2, languagePicked: true })
          }
          placeholder={words.release_plan_language}
        />
      )}
      <KButton
        dataTestId="co-manager-plan-cover"
        text={row.cover ? `${words.release_plan_cover_added} · ${row.cover.name}` : words.release_plan_cover}
        leftIcon="/account_icons/attachment-line.svg"
        onClick={() => {
          setCoverTarget(index)
          coverInputRef.current?.click()
        }}
        disabled={saving}
        background="#FFF"
        hoverBackground="#FAFAFA"
        border="1px solid #E6E6E6"
        hoverBorder="1px solid #D6D6D6"
        textColor="#111"
        width="auto"
        height="32px"
        padding="6px 12px"
        gap="6px"
        borderRadius={100}
        fontSize={12}
        shadowDisabled
      />
    </div>
  )

  if (saved) {
    return (
      <div className="co-manager-claim-form">
        <KSpan text={words.release_plan_title} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
        <KSpan
          text={source === "page" ? words.release_plan_saved_page : words.release_plan_saved}
          fontSize={12}
          lineHeight="16px"
          color="#666"
        />
      </div>
    )
  }

  const needsProductionLines = mode === "new" && options && !options.hasProductionLines

  // One plan at a time: while a planned release is still a draft, the card points at it instead of
  // opening more drafts.
  const openDraft = options?.openDraftPlan
  if (openDraft) {
    return (
      <div className="co-manager-claim-form">
        <KSpan text={words.release_plan_title} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
        <KSpan
          text={fill(words.release_plan_open_draft, { title: openDraft.title })}
          fontSize={12}
          lineHeight="16px"
          color="#666"
        />
        <div className="self-start">
          <KButton
            dataTestId="co-manager-plan-open-draft"
            text={words.release_plan_open_draft_go}
            onClick={() => navigate(releasePath(openDraft.releaseId, "draft"))}
            background="transparent"
            shadowDisabled
            width="auto"
            height="auto"
            padding="0px"
            fontSize={12}
            textColor="#6B7280"
          />
        </div>
      </div>
    )
  }

  return (
    <div className="co-manager-claim-form">
      <KSpan text={words.release_plan_title} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
      {label(words.release_plan_hint)}
      {options && !options.canOpenDrafts && label(words.release_plan_no_new_release)}
      <div className="co-manager-plan-chips">
        {options?.canOpenDrafts !== false &&
          chip(words.release_plan_new, mode === "new", () => setMode("new"), "co-manager-plan-mode-new")}
        {chip(
          words.release_plan_existing,
          mode === "existing",
          () => setMode("existing"),
          "co-manager-plan-mode-existing"
        )}
      </div>

      {needsProductionLines && (
        <>
          {label(words.release_plan_production_lines)}
          <KButton
            text={words.release_plan_production_lines_open}
            onClick={() => navigate(COPYRIGHT_PATH)}
            background="transparent"
            shadowDisabled
            width="auto"
            height="auto"
            padding="0px"
            fontSize={12}
            textColor="#6B7280"
          />
        </>
      )}

      {/* Nothing to fill in until the options say what the account may open and on which label. */}
      {!options && !error && label(words.release_plan_loading)}

      {mode === "new" && options && !needsProductionLines && (
        <>
          {!!options?.defaults?.releaseTitle &&
            label(fill(words.release_plan_defaults, { title: options.defaults.releaseTitle }))}
          {/* The date comes first: the rest of the plan is worked out from it, so it stays closed until then. */}
          <div className="co-manager-plan-row">
            {label(words.release_plan_first_date)}
            {datePicker(cadenceStart, pickStart, "co-manager-plan-cadence-date")}
            {!cadenceStart
              ? label(words.release_plan_start_hint)
              : label(fill(words.release_plan_date_hint, { days: String(options?.minLeadDays ?? 7) }))}
            {!!cadenceStart && (
              <>
                {label(words.release_plan_cadence_how_many)}
                <div className="co-manager-plan-chips">
                  {CADENCE_COUNTS.map((count) =>
                    chip(
                      count === 1
                        ? words.release_plan_cadence_count_one
                        : fill(words.release_plan_cadence_count, { count: String(count) }),
                      !customCount && rows.length === count,
                      () => {
                        setCustomCount("")
                        pickCount(count)
                      },
                      `co-manager-plan-cadence-count-${count}`
                    )
                  )}
                  {customBox(
                    customCount,
                    words.release_plan_cadence_custom_count,
                    MAX_ROWS,
                    (text, value) => {
                      setCustomCount(text)
                      if (value) pickCount(value)
                    },
                    "co-manager-plan-cadence-count"
                  )}
                </div>
                {rows.length > 1 && (
                  <>
                    {label(words.release_plan_cadence_how_often)}
                    <div className="co-manager-plan-chips">
                      {CADENCE_MONTHS.map((months) =>
                        chip(
                          fill(words.release_plan_cadence_every, { months: String(months) }),
                          !customMonths && cadenceMonths === months,
                          () => {
                            setCustomMonths("")
                            pickMonths(months)
                          }
                        )
                      )}
                      {customBox(
                        customMonths,
                        words.release_plan_cadence_custom_months,
                        MAX_CADENCE_MONTHS,
                        (text, value) => {
                          setCustomMonths(text)
                          if (value) pickMonths(value)
                        },
                        "co-manager-plan-cadence-months"
                      )}
                    </div>
                  </>
                )}
                {label(words.release_plan_cadence_hint)}
              </>
            )}
          </div>
          {!!cadenceStart && rows.map(renderRow)}
          {!!cadenceStart && label(words.release_plan_cover_rules)}
          {!!cadenceStart && rows.length < MAX_ROWS && (
            <KButton
              dataTestId="co-manager-plan-add-row"
              text={words.release_plan_add_another}
              onClick={() => {
                setCustomCount("")
                pickCount(rows.length + 1)
              }}
              disabled={saving}
              background="transparent"
              shadowDisabled
              width="auto"
              height="auto"
              padding="0px"
              fontSize={12}
              textColor="#6B7280"
            />
          )}
          {/* The cover picker every row opens: KButton clicks it, so nothing here is a bare control. */}
          <input
            ref={coverInputRef}
            type="file"
            accept={COVER_TYPES.join(",")}
            className="hidden"
            onChange={(event) => {
              readCover(event.target.files?.[0])
              // Cleared so picking the same file twice in a row still fires a change.
              event.target.value = ""
            }}
          />
        </>
      )}

      {mode === "existing" &&
        (releaseOptions.length ? (
          <KDropdown
            dataTestId="co-manager-plan-existing"
            options={releaseOptions}
            selected={releaseOptions.find((option) => option.value === existingId)}
            onSelect={(selected) => setExistingId(((selected as KSelectOption)?.value as number) || null)}
            placeholder={words.release_plan_existing_pick}
          />
        ) : (
          options && label(words.release_plan_existing_none)
        ))}

      {!!error && <KSpan text={error} fontSize={12} lineHeight="16px" color="#E5484D" />}

      <div className="co-manager-bubble-actions">
        <KButton
          dataTestId="co-manager-plan-save"
          text={saving ? words.release_plan_creating : words.release_plan_create}
          onClick={() => save()}
          disabled={saving || !options || !!needsProductionLines || (mode === "new" && !cadenceStart)}
          background="#111"
          textColor="#FFF"
          width="auto"
          height="34px"
          padding="7px 16px"
          borderRadius={100}
          fontSize={14}
        />
      </div>
      <ImageUploadModal
        open={!!beatportLabel}
        labelName={beatportLabel?.label || ""}
        onClose={() => setBeatportLabel(null)}
        onUploadSuccess={() => {
          setBeatportLabel(null)
          // Read the logos again so the label now counts as having one, then carry on with the save.
          fetchLabelBeatportLabels().then(() => setError(""))
        }}
        onSkipBeatport={() => {
          const skipped = beatportLabel ? [...beatportSkipped, beatportLabel.value] : beatportSkipped
          setBeatportSkipped(skipped)
          setBeatportLabel(null)
          save(skipped)
        }}
      />
    </div>
  )
}

export default ReleasePlanCard
